import { TableGridMode } from "consts/table";
import { memo } from "react";
import { Resource, type ResourceProps } from "ui/widgets/Resource";
import { Filters, multi, single, Status } from "consts/users";
import { Filters as MerchantFilters } from "consts/merchants";
import { FilterVariant } from "consts/filters";
import { Option } from "util/option";
import { useTypedSelector } from "hooks";
import { selectMerchantRoleOptions } from "features/users/selectors";
import useUserRoles from "features/users/useUserRoles";
import MerchantUserCreateEdit from "./MerchantUserCreateEdit";
import ToolbarFilters, {
    activeInactiveSelectorKey,
    useToolbarFiltersContext
} from "../shared/ToolbarFilters";

type Props = Pick<
    ResourceProps,
    | 'selectIsTableUninitialized'
    | 'selectIsTableLoading'
    | 'selectTableDataSlice'
    | 'useResource'
    | 'DetailProps'
    | 'adapters'
    | 'LayoutProps'
    | 'columns'
    | 'pinnedColumns'
    | 'Toolbar'
>;

const MerchantUsers = ({
    selectIsTableUninitialized,
    selectIsTableLoading,
    selectTableDataSlice,
    DetailProps,
    useResource,
    adapters,
    LayoutProps,
    columns,
    pinnedColumns,
    Toolbar
}: Props) => {
    useUserRoles();
    const { filtersRef } = useToolbarFiltersContext();
    const merchantRoleOptions = useTypedSelector(selectMerchantRoleOptions);

    return (
        <Resource
            name='Merchant User'
            useResource={useResource}
            mode={TableGridMode.MerchantUser}
            selectIsTableUninitialized={selectIsTableUninitialized}
            selectIsTableLoading={selectIsTableLoading}
            selectTableDataSlice={selectTableDataSlice}
            Toolbar={Toolbar}
            DetailProps={DetailProps}
            Detail={MerchantUserCreateEdit}
            LayoutProps={LayoutProps}
            adapters={adapters}
            pinnedColumns={pinnedColumns}
            columns={columns}
            filtersRef={filtersRef}
            FiltersProps={{
                hiddenSelectedFilterIds: [Filters.status],
                useVisibilityRules: () => filter =>
                    filter.props.id !== Filters.status,
                sx: {
                    '& .MuiCollapse-wrapperInner > .MuiBox-root > .MuiStack-root': {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))',
                        alignItems: 'start'
                    }
                },
                filtersModelOverrides: {
                    [Filters.roleId]: merchantRoleOptions
                }
            }}
            filterTypes={new Map<FilterVariant, string[]>([
                [FilterVariant.SingleOption, single],
                [FilterVariant.MultiOption, [
                    ...multi,
                    MerchantFilters.merchantId
                ]],
                // Sent as email=<value>, a "contains" match done by GET /users
                [FilterVariant.Text, [Filters.email]]
            ])}
        />
    );
};

export default memo((props: Props) => (
    <ToolbarFilters
        toolbarFilters={new Map([
            [activeInactiveSelectorKey, {
                filterId: Filters.status,
                options: [
                    Option.make(Status.Active),
                    Option.make(Status.Inactive)
                ]
            }]
        ])}
    >
        <MerchantUsers
            {...props}
        />
    </ToolbarFilters>
));
